import React, { useState } from "react";
import {
  Card,
  CardContent,
  Typography,
  Box,
  Chip,
  IconButton,
  LinearProgress,
  Avatar,
  Collapse,
} from "@mui/material";
import {
  Visibility as VisibilityIcon,
  DragIndicator as DragIcon,
  Person as PersonIcon,
  AccessTime as AccessTimeIcon,
  CloudUpload as CloudUploadIcon,
} from "@mui/icons-material";
import type { JobResponse } from "@taxassist/api-client";
import { JobStatus } from "@taxassist/api-client";
import { StatusBadge } from "../jobs/StatusBadge";
import { FilingTypeBadge } from "../jobs/FilingTypeBadge";
import { formatJobDate, formatJobReference } from "../../utils/jobUtils";

interface AdaptiveJobCardProps {
  job: JobResponse;
  isCompact: boolean;
  onUploadFiles?: (job: JobResponse) => void;
  onViewDetails?: (job: JobResponse) => void;
}

export const AdaptiveJobCard = React.memo(
  ({ job, isCompact, onUploadFiles, onViewDetails }: AdaptiveJobCardProps) => {
    const [isExpanded, setIsExpanded] = useState(false);

    const handleCardClick = React.useCallback(() => {
      onViewDetails?.(job);
    }, [job, onViewDetails]);

    const handleCardHover = () => {
      if (isCompact) {
        setIsExpanded(true);
      }
    };

    const handleCardLeave = () => {
      if (isCompact) {
        setIsExpanded(false);
      }
    };

    // Calculate ETA for queued and processing jobs
    const showETA =
      job.status === JobStatus.TRIAGE ||
      job.status === JobStatus.PROCESSING ||
      job.status === JobStatus.SUBMITTED ||
      job.status === JobStatus.VERIFICATION;

    // Calculate ETA from actual etaNum value
    const getETA = () => {
      if (!showETA || !job.etaNum) return null;

      const now = Date.now();
      const etaDate = new Date(job.etaNum * 1000); // Convert Unix timestamp to Date
      const diffMs = etaDate.getTime() - now;

      if (diffMs < 0) {
        // Overdue
        const overdueDays = Math.ceil(Math.abs(diffMs) / (1000 * 60 * 60 * 24));
        return overdueDays === 1 ? "1d overdue" : `${overdueDays}d overdue`;
      }

      const diffHours = Math.ceil(diffMs / (1000 * 60 * 60));
      if (diffHours < 24) {
        return `${diffHours}h`;
      } else {
        const diffDays = Math.ceil(diffHours / 24);
        return `${diffDays}d`;
      }
    };

    const eta = React.useMemo(() => getETA(), [showETA, job.etaNum]);

    // Compact card content
    const CompactContent = () => (
      <Box
        sx={{ display: "flex", alignItems: "center", gap: 1, width: "100%" }}
      >
        {/* Job Info */}
        <Box
          sx={{
            flex: 1,
            minWidth: 0,
            display: "flex",
            alignItems: "center",
            gap: 1,
          }}
        >
          <Typography
            variant="caption"
            fontWeight={600}
            color="primary.main"
            sx={{
              whiteSpace: "nowrap",
              fontSize: "0.8rem",
            }}
          >
            {formatJobReference(job)}
          </Typography>

          <Typography
            variant="caption"
            sx={{
              flex: 1,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              color: "text.primary",
              fontSize: "0.8rem",
            }}
          >
            {job.clientName}
          </Typography>

          <FilingTypeBadge
            filingType={job.filingType}
            showLabel={false}
            size="small"
          />

          <Chip
            label={job.taxYear}
            size="small"
            sx={{
              fontSize: "0.65rem",
              height: 18,
              "& .MuiChip-label": { px: 0.75 },
            }}
          />

          {/* ETA in compact view */}
          {showETA && eta && (
            <Box
              sx={{ display: "flex", alignItems: "center", gap: 0.25, ml: 0.5 }}
            >
              <AccessTimeIcon sx={{ fontSize: 12, color: "warning.main" }} />
              <Typography
                variant="caption"
                sx={{
                  fontSize: "0.7rem",
                  color: "warning.main",
                  fontWeight: 600,
                }}
              >
                {eta}
              </Typography>
            </Box>
          )}
        </Box>

        {/* Removed eye icon - accordion expansion shows details on hover */}
      </Box>
    );

    // Expanded content (additional details)
    const ExpandedContent = () => (
      <Collapse in={isExpanded} timeout={200}>
        <Box sx={{ px: 1.5, pb: 1 }}>
          {/* Client Name with Avatar */}
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
            <Avatar sx={{ width: 20, height: 20, fontSize: "0.7rem" }}>
              <PersonIcon sx={{ fontSize: 14 }} />
            </Avatar>
            <Typography variant="body2" fontWeight={500}>
              {job.clientName}
            </Typography>
          </Box>

          {/* Status Badge */}
          <Box sx={{ mb: 1 }}>
            <StatusBadge status={job.status} size="small" job={job} />
          </Box>

          {/* Actions */}
          <Box sx={{ display: "flex", gap: 1 }}>
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                onViewDetails?.(job);
              }}
              sx={{
                bgcolor: "action.hover",
                "&:hover": { bgcolor: "action.selected" },
              }}
            >
              <VisibilityIcon fontSize="small" />
            </IconButton>
            {onUploadFiles && (
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  onUploadFiles(job);
                }}
                sx={{
                  bgcolor: "action.hover",
                  "&:hover": { bgcolor: "action.selected" },
                }}
              >
                <CloudUploadIcon fontSize="small" />
              </IconButton>
            )}
          </Box>
        </Box>
      </Collapse>
    );

    if (!isCompact) {
      // Standard full card (existing behavior)
      return (
        <Card
          onClick={handleCardClick}
          sx={{
            cursor: "pointer",
            transition: "all 0.2s ease-in-out",
            "&:hover": {
              elevation: 4,
              transform: "translateY(-2px)",
              borderColor: "primary.main",
            },
            border: "1px solid",
            borderColor: "divider",
            position: "relative",
          }}
        >
          <CardContent sx={{ p: 1.5, "&:last-child": { pb: 1.5 } }}>
            {/* Header */}
            <Box
              sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}
            >
              <Typography
                variant="subtitle2"
                fontWeight={600}
                color="primary.main"
              >
                {formatJobReference(job)}
              </Typography>
              <StatusBadge status={job.status} size="small" job={job} />
            </Box>

            {/* Client Name */}
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
              <Avatar sx={{ width: 20, height: 20, fontSize: "0.7rem" }}>
                <PersonIcon sx={{ fontSize: 14 }} />
              </Avatar>
              <Typography variant="body2" fontWeight={500}>
                {job.clientName}
              </Typography>
            </Box>

            {/* Filing Type and Year */}
            <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
              <FilingTypeBadge
                filingType={job.filingType}
                showLabel={true}
                size="small"
              />
              <Chip
                label={job.taxYear || "No Year"}
                size="small"
                variant="outlined"
              />

              {/* ETA for queued/processing jobs */}
              {showETA && eta && (
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 0.5,
                    ml: "auto",
                  }}
                >
                  <AccessTimeIcon
                    sx={{ fontSize: 14, color: "warning.main" }}
                  />
                  <Typography
                    variant="caption"
                    color="warning.main"
                    fontWeight={600}
                  >
                    {eta}
                  </Typography>
                </Box>
              )}
            </Box>
          </CardContent>
        </Card>
      );
    }

    // Compact card with in-place accordion expansion
    return (
      <Card
        onClick={handleCardClick}
        onMouseEnter={handleCardHover}
        onMouseLeave={handleCardLeave}
        sx={{
          cursor: "pointer",
          transition: "all 0.2s ease-in-out",
          "&:hover": {
            bgcolor: "action.hover",
            borderColor: "primary.main",
            boxShadow: 2,
          },
          border: "1px solid",
          borderColor: isExpanded ? "primary.main" : "divider",
          mb: 0.5,
          overflow: "hidden", // Ensure smooth accordion animation
        }}
      >
        {/* Compact Content (always visible) */}
        <CardContent sx={{ p: 1, "&:last-child": { pb: isExpanded ? 0 : 1 } }}>
          <CompactContent />
        </CardContent>

        {/* Expanded Content (accordion) */}
        <ExpandedContent />
      </Card>
    );
  },
);
